import { Component, EventEmitter, inject, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { TransactionForm } from "@bitwarden/web-vault/app/models/types/account.types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { transactionDataType } from "@bitwarden/web-vault/app/models/types/transactionData.types";

interface AccountWithTransactions {
  account: AccountView;
  transactions: transactionDataType[];
  expanded: boolean;
}

@Component({
  selector: "app-add-transaction-stage",
  templateUrl: "./add-transaction-stage.component.html",
})
export class AddTransactionStageComponent implements OnInit {
  @Input() linkedAccounts: AccountView[] = [];
  /** Transactions parsed from a mapped statement import; pre-fills the per-account lists. */
  @Input() prefillTransactions: transactionDataType[] = [];
  @Output() transactionsChanged = new EventEmitter<transactionDataType[]>();

  protected readonly TransactionDirection = TransactionDirection;

  private formBuilder = inject(FormBuilder);
  private i18nService = inject(I18nService);

  // Stable In/Out labels (used as dropdown options and for mapping selections back to the enum)
  readonly inLabel = this.i18nService.t("in");
  readonly outLabel = this.i18nService.t("out");
  readonly directionOptions = [this.inLabel, this.outLabel];

  accountsWithTransactions: AccountWithTransactions[] = [];
  transactionForms: Map<string, FormGroup> = new Map();

  ngOnInit(): void {
    this.initializeAccountsWithTransactions();
    // Surface prefilled rows to the wizard so they're saved even if the user never edits this stage.
    if (this.prefillTransactions.length) {
      this.emitAllTransactions();
    }
  }

  private initializeAccountsWithTransactions(): void {
    this.accountsWithTransactions = this.linkedAccounts.map((account) => ({
      account,
      transactions: this.prefillTransactions.filter((t) => t.account?.id === account.id),
      expanded: true,
    }));

    // Create a form for each account
    this.linkedAccounts.forEach((account) => {
      const form = this.formBuilder.group({
        description: ["", Validators.required],
        date: ["", Validators.required],
        direction: [TransactionDirection.In, Validators.required],
        amount: [null, [Validators.required, Validators.min(0.01)]],
        symbol: [account.currency || ""],
      });
      this.transactionForms.set(account.id, form);
    });
  }

  getFormForAccount(accountId: string): FormGroup {
    return this.transactionForms.get(accountId);
  }

  isManuallyAdded(account: AccountView): boolean {
    return account?.origin === Origin.manual;
  }

  selectedDirectionLabel(accountId: string): string {
    const value = this.transactionForms.get(accountId)?.controls["direction"].value;
    return value === TransactionDirection.Out ? this.outLabel : this.inLabel;
  }

  onDirectionSelected(accountId: string, label: string): void {
    const direction = label === this.inLabel ? TransactionDirection.In : TransactionDirection.Out;
    this.transactionForms.get(accountId)?.controls["direction"].setValue(direction);
  }

  toggleExpanded(accountId: string): void {
    const accountWithTrans = this.accountsWithTransactions.find((a) => a.account.id === accountId);
    if (accountWithTrans) {
      accountWithTrans.expanded = !accountWithTrans.expanded;
    }
  }

  addTransaction(accountId: string): void {
    const form = this.transactionForms.get(accountId);
    const accountWithTrans = this.accountsWithTransactions.find((a) => a.account.id === accountId);

    if (form?.valid && accountWithTrans) {
      const formValue = form.value;

      const transactionForm: TransactionForm = {
        amount: formValue.amount,
        quantity: formValue.amount,
        symbol: formValue.symbol,
        currency: accountWithTrans.account.currency,
        date: formValue.date,
        transactionStatus: TransactionStatusEnum.transaction,
        description: formValue.description,
        type: formValue.direction,
      };

      const transactionData: transactionDataType = {
        account: accountWithTrans.account,
        formData: transactionForm,
      };

      accountWithTrans.transactions.push(transactionData);

      // Emit all transactions
      this.emitAllTransactions();

      // Reset form
      form.reset({
        direction: TransactionDirection.In,
        symbol: accountWithTrans.account.currency || "",
      });
    } else {
      // Mark fields as touched to show errors
      Object.keys(form.controls).forEach((key) => {
        form.controls[key].markAsTouched();
        form.controls[key].markAsDirty();
      });
    }
  }

  /**
   * Commits any row the user filled in but didn't click "Add" on (called by the wizard
   * before Complete, so an in-progress transaction isn't silently dropped). Returns false
   * if a filled row is invalid, so the wizard can block and the field errors stay visible.
   */
  commitPendingTransactions(): boolean {
    let allValid = true;
    for (const awt of this.accountsWithTransactions) {
      const form = this.transactionForms.get(awt.account.id);
      if (!form || !this.isFormFilled(form)) {
        continue;
      }
      if (!form.valid) {
        allValid = false;
      }
      // addTransaction commits + emits + resets when valid, or marks errors when not.
      this.addTransaction(awt.account.id);
    }
    return allValid;
  }

  private isFormFilled(form: FormGroup): boolean {
    const v = form.value;
    return (
      !!v.description ||
      !!v.date ||
      (v.amount !== null && v.amount !== undefined && v.amount !== "")
    );
  }

  removeTransaction(accountId: string, index: number): void {
    const accountWithTrans = this.accountsWithTransactions.find((a) => a.account.id === accountId);
    if (accountWithTrans) {
      accountWithTrans.transactions.splice(index, 1);
      this.emitAllTransactions();
    }
  }

  private emitAllTransactions(): void {
    const allTransactions: transactionDataType[] = [];
    this.accountsWithTransactions.forEach((awt) => {
      allTransactions.push(...awt.transactions);
    });
    this.transactionsChanged.emit(allTransactions);
  }
}
